/**
 * Retry policy for the GDN-1538 explicit-end-session call.
 *
 * The backend endpoint is idempotent — re-calls return success with
 * `room_deleted: false` after the first successful run. That means
 * the retry policy can be aggressive without worrying about
 * double-completing queue entries.
 *
 * Contract the caller relies on:
 *
 * - One retry on transient failure (~1s backoff).
 * - Total wait capped at ~3 seconds before the caller gives up and
 *   proceeds with local cleanup. This is the FE invariant: the
 *   provider must NEVER be blocked from leaving by a slow backend.
 * - Returns `{ ok: true }` on success, `{ ok: false, error }` on
 *   failure-after-retries or hard timeout. Caller decides what to do
 *   on `ok: false` (typically Sentry + proceed).
 *
 * Why a free-standing helper rather than inline in
 * `TelehealthCallManager`: timing-sensitive promise dance, easier to
 * unit test in isolation (see the sibling test file).
 */

const RETRY_BACKOFF_MS = 1000;
const TOTAL_WAIT_BUDGET_MS = 3000;

export interface EndSessionRetryArgs {
  /** Caller-provided trigger — typically the RTK mutation's `unwrap()`-able promise factory. */
  call: () => Promise<unknown>;
  /** Override for testing. Defaults to ``setTimeout``-based sleep. */
  delay?: (ms: number) => Promise<void>;
  /** Override for testing. Defaults to ``Date.now``. */
  now?: () => number;
}

export type EndSessionRetryResult =
  | { ok: true; attempts: number }
  | {
      ok: false;
      attempts: number;
      reason: 'failed' | 'budget_exceeded';
      error: unknown;
    };

function defaultDelay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const TIMEOUT_SENTINEL = Symbol('end_session_retry_timeout');

/**
 * Race the caller's promise against a wall-clock cap. Resolves to
 * the original promise's value if it lands first, or
 * ``TIMEOUT_SENTINEL`` if the budget ran out. Rejections from
 * ``promise`` propagate, so callers must wrap in try/catch.
 *
 * Implemented in terms of the injectable ``delay`` so tests can
 * drive the timeout deterministically.
 */
async function raceAgainstBudget<T>(
  promise: Promise<T>,
  timeoutMs: number,
  delay: (ms: number) => Promise<void>,
): Promise<T | typeof TIMEOUT_SENTINEL> {
  // ``Promise.race`` widens ``unique symbol`` back to ``symbol`` in its
  // inferred result type, which then fails to satisfy the declared
  // ``T | typeof TIMEOUT_SENTINEL``. Identity comparison against
  // ``TIMEOUT_SENTINEL`` at the call sites is what actually narrows
  // the value at runtime, so a cast back to the precise union is safe.
  return Promise.race([
    promise,
    delay(timeoutMs).then(() => TIMEOUT_SENTINEL),
  ]) as unknown as Promise<T | typeof TIMEOUT_SENTINEL>;
}

/**
 * Run `call` with one retry on failure and a hard 3s wall clock cap.
 *
 * Two failure modes:
 *
 *  - `reason: 'failed'`: both attempts errored before the budget ran
 *    out. Caller should Sentry-capture and proceed with local cleanup.
 *  - `reason: 'budget_exceeded'`: the call(s) were still in flight
 *    when the 3s budget ran out, or there was no room left for the
 *    backoff + retry. Caller should "fire-and-forget" proceed; the
 *    call may still complete server-side (the endpoint is idempotent
 *    so leftover state will be cleaned by the next successful run,
 *    the room.deleted webhook, or the room TTL).
 *
 * Implementation notes:
 *  - Rejections from ``call`` are caught explicitly so they don't
 *    short-circuit the budget race; the budget only fires when the
 *    call hangs.
 *  - Each attempt is given the *remaining* budget rather than a
 *    fixed slice; a slow first attempt that returns 2900ms in still
 *    leaves a tiny window for the retry, which we honor.
 */
export async function endTelehealthSessionWithRetry({
  call,
  delay = defaultDelay,
  now = Date.now,
}: EndSessionRetryArgs): Promise<EndSessionRetryResult> {
  const startedAt = now();

  // Attempt 1. ``call()`` may reject; catch synchronously so the
  // rejection doesn't race the budget.
  const firstBudget = TOTAL_WAIT_BUDGET_MS - (now() - startedAt);
  // Defensive: `now() - startedAt` is ~0 here in normal operation, so
  // `firstBudget <= 0` only trips on clock skew (e.g. `Date.now()`
  // jumping backwards between the two reads). Cheap to keep, prevents
  // a negative budget being passed into the race below.
  if (firstBudget <= 0) {
    return { ok: false, attempts: 0, reason: 'budget_exceeded', error: null };
  }
  let firstError: unknown = null;
  const firstPromise: Promise<'ok' | 'error'> = Promise.resolve()
    .then(call)
    .then(() => 'ok' as const)
    .catch(err => {
      firstError = err;
      return 'error' as const;
    });
  const firstOutcome = await raceAgainstBudget(firstPromise, firstBudget, delay);
  if (firstOutcome === 'ok') {
    return { ok: true, attempts: 1 };
  }
  if (firstOutcome === TIMEOUT_SENTINEL) {
    // First attempt is still in flight at the budget. Don't try a
    // second time — there's no time for it.
    return {
      ok: false,
      attempts: 1,
      reason: 'budget_exceeded',
      error: null,
    };
  }

  // firstOutcome === 'error' — attempt a retry after backoff if the
  // remaining budget allows it.
  const remainingAfterFirst = TOTAL_WAIT_BUDGET_MS - (now() - startedAt);
  if (remainingAfterFirst <= 0) {
    return {
      ok: false,
      attempts: 1,
      reason: 'budget_exceeded',
      error: firstError,
    };
  }
  const backoff = Math.min(RETRY_BACKOFF_MS, remainingAfterFirst);
  await delay(backoff);

  const remainingAfterBackoff = TOTAL_WAIT_BUDGET_MS - (now() - startedAt);
  if (remainingAfterBackoff <= 0) {
    return {
      ok: false,
      attempts: 1,
      reason: 'budget_exceeded',
      error: firstError,
    };
  }

  let secondError: unknown = null;
  const secondPromise: Promise<'ok' | 'error'> = Promise.resolve()
    .then(call)
    .then(() => 'ok' as const)
    .catch(err => {
      secondError = err;
      return 'error' as const;
    });
  const secondOutcome = await raceAgainstBudget(
    secondPromise,
    remainingAfterBackoff,
    delay,
  );
  if (secondOutcome === 'ok') {
    return { ok: true, attempts: 2 };
  }
  if (secondOutcome === TIMEOUT_SENTINEL) {
    return {
      ok: false,
      attempts: 2,
      reason: 'budget_exceeded',
      error: firstError,
    };
  }
  return { ok: false, attempts: 2, reason: 'failed', error: secondError };
}
